/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Image, Text, SimpleGrid} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import {getPDPath} from '../../../utils/custom-utils'

/**
 * Image with text component
 *
 * @param {object} props
 * @param {string} - props.ITCLink - Image Link.
 * @param {string} - props.ITCText - Text Below Image.
 * @param {image} - props.image - Image.
 * @param {string} - props.heading - Text Overlay.
 * @param {string} - props.alt - The image alt text shown by the component.
 * @returns {React.ReactElement} - HeroBanner component.
 */
export const Blog = ({
    ITCLink,
    mainBlogHeading,
    mainLink,
    mainText,
    blog1Heading,
    blog1Description,
    blog1ImageAltTag,
    blog1Link,
    blog1Text,
    blog2Heading,
    blog2Description,
    blog2ImageAltTag,
    blog2Link,
    blog2Text,
    blog3Heading,
    blog3Description,
    blog3ImageAltTag,
    blog3Link,
    blog3Text,
    custom
}) => {
    const hasCaption = mainBlogHeading
    const {site, locale} = useMultiSite()
    const blogPath = getPDPath(ITCLink, site, locale)
    const segment = useSegment()

    return (
        <Box className={'image-with-text'}>
            <SimpleGrid columns={{base: 1, sm: 2}}>
                <Box>
                    {hasCaption && (
                        <Text>
                            {mainBlogHeading && (
                                <Box>
                                    <Text>
                                        {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                        {/* The HTML in the response from Page Designer API is already sanitized. */}
                                        <Box
                                            dangerouslySetInnerHTML={{
                                                __html: mainBlogHeading
                                            }}
                                        />
                                    </Text>
                                </Box>
                            )}
                            {mainLink && <Link href={mainLink}>{mainText}</Link>}
                        </Text>
                    )}
                    {blog1Heading && (
                        <Text>
                            {blog1Heading && (
                                <Text
                                    className={'image-with-text-text-underneath'}
                                    textColor="white"
                                    fontSize="md"
                                    fontWeight="semibold"
                                    lineHeight={'1.5'}
                                    mb={'1rem'}
                                >
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: blog1Heading
                                        }}
                                    />
                                </Text>
                            )}
                            {blog1Description && (
                                <Text
                                    className={'image-with-text-text-underneath'}
                                    textColor="black"
                                    fontSize="md"
                                    fontWeight="normal"
                                    lineHeight={'1.5'}
                                    mb={'1rem'}
                                >
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: blog1Description
                                        }}
                                    />
                                </Text>
                            )}
                            {blog1Link && <Link href={blog1Link}>{blog1Text}</Link>}
                        </Text>
                    )}
                    {blog2Heading && (
                        <Text>
                            {blog2Heading && (
                                <Text
                                    className={'image-with-text-text-underneath'}
                                    textColor="black"
                                    fontSize="md"
                                    fontWeight="normal"
                                    lineHeight={'1.5'}
                                    mb={'1rem'}
                                >
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: blog2Heading
                                        }}
                                    />
                                </Text>
                            )}
                            {blog2Description && (
                                <Text
                                    className={'image-with-text-text-underneath'}
                                    textColor="black"
                                    fontSize="md"
                                    fontWeight="normal"
                                    lineHeight={'1.5'}
                                    mb={'1rem'}
                                >
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: blog2Description
                                        }}
                                    />
                                </Text>
                            )}
                            {blog2Link && <Link href={blog2Link}>{blog2Text}</Link>}
                        </Text>
                    )}
                    {blog3Heading && (
                        <Text>
                            {blog3Heading && (
                                <Text
                                    className={'image-with-text-text-underneath'}
                                    textColor="black"
                                    fontSize="md"
                                    fontWeight="normal"
                                    lineHeight={'1.5'}
                                    mb={'1rem'}
                                >
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: blog3Heading
                                        }}
                                    />
                                </Text>
                            )}
                            {blog3Description && (
                                <Text
                                    className={'image-with-text-text-underneath'}
                                    textColor="black"
                                    fontSize="md"
                                    fontWeight="normal"
                                    lineHeight={'1.5'}
                                    mb={'1rem'}
                                >
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: blog3Description
                                        }}
                                    />
                                </Text>
                            )}
                            {blog3Link && <Link href={blog3Link}>{blog3Text}</Link>}
                        </Text>
                    )}
                </Box>
                <Box
                    className={'image-with-text-figure'}
                    width={{base: '100%', sm: '60%'}}
                    onClick={(event) => {
                        // segment
                        segment.sendPanelSelectedEvent(event, {})
                    }}
                >
                    <Link to={blogPath}>
                        <Image
                            className={'image-with-text-image'}
                            width={'100%'}
                            ignoreFallback={true}
                            alt={blog1ImageAltTag}
                            src={custom?.blog1Image?.disBaseLink}
                        />
                    </Link>
                    <Link to={blogPath}>
                        <Image
                            className={'image-with-text-image'}
                            width={'100%'}
                            ignoreFallback={true}
                            alt={blog2ImageAltTag}
                            src={custom?.blog2Image?.disBaseLink}
                        />
                    </Link>
                    <Link to={blogPath}>
                        <Image
                            className={'image-with-text-image'}
                            width={'100%'}
                            ignoreFallback={true}
                            alt={blog3ImageAltTag}
                            src={custom?.blog3Image?.disBaseLink}
                        />
                    </Link>
                </Box>
            </SimpleGrid>
        </Box>
    )
}

Blog.propTypes = {
    ITCLink: PropTypes.string,
    mainBlogHeading: PropTypes.string,
    mainLink: PropTypes.string,
    mainText: PropTypes.string,
    blog1Heading: PropTypes.string,
    blog1ImageAltTag: PropTypes.string,
    blog1Link: PropTypes.string,
    blog1Text: PropTypes.string,
    blog1Description: PropTypes.string,
    blog2Heading: PropTypes.string,
    blog2ImageAltTag: PropTypes.string,
    blog2Link: PropTypes.string,
    blog2Text: PropTypes.string,
    blog2Description: PropTypes.string,
    blog3Heading: PropTypes.string,
    blog3ImageAltTag: PropTypes.string,
    blog3Link: PropTypes.string,
    blog3Text: PropTypes.string,
    blog3Description: PropTypes.string,
    custom: PropTypes.object
}

export default Blog
